// The rival's slow game: forging the player out of an alliance.
//
// Killing the player never sticks and neither does killing a teacher — so the
// deepest thing Aldric Vane can do is not a sword at all. Each loop, once he
// has watched the player long enough to know which realms they lean on, he
// lays convincing forgeries before one of those courts: letters in the
// player's own hand promising them to an enemy. Where his ambushes escalate on
// a fixed ladder (see antiPlayerEngine), this runs *every* loop he is active,
// because it is the one move that can quietly cost the player the whole run —
// a realm that believes you sold them will not march for you this year, and a
// year is all you have.
//
// The player's only answer is to be standing in front of the leader when the
// accusation is read and talk their way clear of it. Get an answer wrong and
// the court calls the guard; lose that fight and you hang. But the questions
// are the same across loops, so what you learned by getting it wrong (and
// dying, and coming back) is exactly the foreknowledge the loop is built on.

import type {
  AccusationTrial,
  FactionId,
  GameState,
  LocationId,
  LoopState,
  PermanentState,
} from "../types"
import { accusationTrials } from "./content"

/** Everything about an accused realm that is the same across all its trial scripts. */
interface AccusationFactionConfig {
  /** Where the leader sits, and so where the trial is faced. */
  seatLocationId: LocationId
  /** The household guard that seizes the player on a wrong answer. */
  battleId: string
  /** The permanent marker recorded once the player has disproved a forgery against this realm. */
  clearedKnowledge: string
}

/**
 * The seven allied realms he can frame, and the ground each accusation is
 * fought on. He only ever forges the player out of an *alliance*, so hostile
 * factions are deliberately absent.
 */
export const ACCUSATION_FACTIONS: Record<string, AccusationFactionConfig> = {
  humans: {
    seatLocationId: "crownreach",
    battleId: "accusation_humans_battle",
    clearedKnowledge: "disproved_forgery_humans",
  },
  high_elves: {
    seatLocationId: "moonspire",
    battleId: "accusation_high_elves_battle",
    clearedKnowledge: "disproved_forgery_high_elves",
  },
  dwarves: {
    seatLocationId: "stonehall",
    battleId: "accusation_dwarves_battle",
    clearedKnowledge: "disproved_forgery_dwarves",
  },
  wood_elves: {
    seatLocationId: "greenveil",
    battleId: "accusation_wood_elves_battle",
    clearedKnowledge: "disproved_forgery_wood_elves",
  },
  spellcasters: {
    seatLocationId: "sevenfold_tower",
    battleId: "accusation_spellcasters_battle",
    clearedKnowledge: "disproved_forgery_spellcasters",
  },
  good_spirits: {
    seatLocationId: "whispering_lake",
    battleId: "accusation_good_spirits_battle",
    clearedKnowledge: "disproved_forgery_good_spirits",
  },
  nomads: {
    seatLocationId: "camp_seven_banners",
    battleId: "accusation_nomads_battle",
    clearedKnowledge: "disproved_forgery_nomads",
  },
}

/** The loop flag that records which realm he has framed this loop. */
export function accusationFlag(factionId: FactionId): string {
  return `accused_by_forgery_${factionId}`
}

/** Set once the trial has been faced this loop — passed or not — so it never re-opens. */
export function accusationFacedFlag(factionId: FactionId): string {
  return `accusation_faced_${factionId}`
}

/** Set once the forgery has been disproved this loop, which lifts the alliance block. */
export function accusationClearedFlag(factionId: FactionId): string {
  return `accusation_cleared_${factionId}`
}

/** The distinct allied realms he has watched the player court, in the order first seen. */
function knownAlliedTargets(permanent: PermanentState): FactionId[] {
  const seen = new Set<FactionId>()
  const targets: FactionId[] = []
  for (const factionId of permanent.antiPlayerState.knownPlayerAllies) {
    if (seen.has(factionId)) continue
    if (!ACCUSATION_FACTIONS[factionId]) continue
    seen.add(factionId)
    targets.push(factionId)
  }
  return targets
}

/**
 * Which realm he frames for a given loop, or null if he cannot yet. He needs to
 * have been met at least once (otherwise nobody is hunting the player), to not
 * have been severed, and to have watched the player court at least one realm —
 * there is no forging a letter to a court the player has never approached. Among
 * the realms he has watched, he rotates by loop so a run of loops does not frame
 * the same court twice running.
 */
export function pickAccusedFactionId(
  permanent: PermanentState,
  loopNumber: number,
): FactionId | null {
  const anti = permanent.antiPlayerState
  if (anti.neutralized) return null
  if (anti.encounterCount < 1) return null
  const targets = knownAlliedTargets(permanent)
  if (targets.length === 0) return null
  return targets[loopNumber % targets.length]
}

/** The realm he has framed this loop, read back off the loop flag, or null. */
export function getAccusedFactionId(loop: LoopState): FactionId | null {
  for (const factionId of Object.keys(ACCUSATION_FACTIONS)) {
    if (loop.worldFlags.includes(accusationFlag(factionId))) return factionId
  }
  return null
}

/** The trial scripts written for a realm. */
function trialsForFaction(factionId: FactionId): AccusationTrial[] {
  return accusationTrials.filter((trial) => trial.factionId === factionId)
}

/**
 * Which script the accused realm uses this loop. Stable for a given loop (so a
 * save reloaded mid-trial shows the same one) and stepped by loop number, so a
 * player who is framed by the same court across several loops does not simply
 * re-read the identical scene.
 */
export function getTrialForLoop(
  factionId: FactionId,
  loopNumber: number,
): AccusationTrial | null {
  const pool = trialsForFaction(factionId)
  if (pool.length === 0) return null
  const targets = Object.keys(ACCUSATION_FACTIONS).length || 1
  // Advance through the pool as loops cycle back around to this realm.
  const step = Math.floor(loopNumber / targets)
  return pool[step % pool.length]
}

/** Whether an accused realm is still holding the forgery against the player this loop. */
export function isFramedUncleared(loop: LoopState, factionId: FactionId): boolean {
  return (
    loop.worldFlags.includes(accusationFlag(factionId)) &&
    !loop.worldFlags.includes(accusationClearedFlag(factionId))
  )
}

/**
 * The trial to open right now, or null. Fires only when the player is standing
 * at the accused leader's seat and has not already faced the trial this loop.
 */
export function getPendingTrial(state: GameState): AccusationTrial | null {
  const { loop } = state
  if (!loop.alive) return null
  const factionId = getAccusedFactionId(loop)
  if (!factionId) return null
  if (loop.worldFlags.includes(accusationFacedFlag(factionId))) return null
  const config = ACCUSATION_FACTIONS[factionId]
  if (loop.currentLocationId !== config.seatLocationId) return null
  return getTrialForLoop(factionId, loop.loopNumber)
}

/** The arrest battle for a trial's realm — begun when the player answers wrongly. */
export function getAccusationBattleId(factionId: FactionId): string | null {
  return ACCUSATION_FACTIONS[factionId]?.battleId ?? null
}

/** The permanent marker recorded when the player disproves a realm's forgery. */
export function getClearedKnowledge(factionId: FactionId): string | null {
  return ACCUSATION_FACTIONS[factionId]?.clearedKnowledge ?? null
}
